/**
 * ── STATIC-MESH COLLISION — THE VOCABULARY, THE RESOLVER, AND THE PURE GEOMETRY (EDITOR v2 · S5) ──
 *
 * PURE (listed in `tests/purity.test.ts`; Node ↔ browser like `schema.ts`): no `three`, no Rapier,
 * no clock. Everything here is checkable in `npm run check` with no GPU and no GLB — which is
 * the point, because the two ways this subsystem fails are both SILENT: a hull that lands one
 * yaw off the mesh it guards reports nothing, and a forgotten `UCX_` node makes a wall the player
 * walks through while every screenshot stays identical.
 *
 * ── RULE 0: WHERE COLLISION COMES FROM, IN ONE PLACE ───────────────────────────────────────────
 *
 * Two places may DECLARE a mode — the world's asset manifest (`WorldFileAssetSchema.collision`)
 * and the placement (`StaticMeshSchema.collision`) — so there is exactly ONE resolver
 * (`resolveCollisionMode`) and it returns its PROVENANCE beside the answer. The Details panel
 * shows `From asset — resolves to trimesh (default)`, not just `trimesh`, because the artist's
 * next question is always "where did that come from".
 *
 * ── THE UE CONVENTION, AND THE FALLBACK ────────────────────────────────────────────────────────
 *
 * `UCX_<mesh>_NN` (convex hull) · `UBX_` (box) · `USP_` (sphere) · `UCP_` (capsule) — nodes named
 * this way INSIDE the source GLB are collision, never rendered: the loader (`entities/staticMesh.ts`)
 * records each as a `CollisionShape` and removes it from the render graph. ONE regex,
 * `COLLISION_PREFIX`, shared with the future cook so the two cannot disagree on what a hull is.
 * An asset with no such nodes falls back to `trimesh` (exact, per render primitive — the honest
 * default for hollow architecture like Sponza, where a convex decomposition would fill the atrium)
 * or `box` (the asset's AABB — a crate). `ucx` with ZERO prefixed nodes resolves to `none` with a
 * warning, never to trimesh: a forgotten hull must be LOUD, not quietly expensive.
 *
 * ── THE SIMILARITY TRANSFORM (D60), POINT SIDE ─────────────────────────────────────────────────
 *
 * A placement is `pos · yawDeg · scale`, and since the non-uniform-scale stage the scale is a
 * `[x, y, z]` here even when the file spelled it as one number (`scaleVec3` is the ONE fold; see
 * `StaticMeshSchema.scale`). Rapier colliders carry NO scale, so the scale is baked into the
 * points (`transformShapes`) and the rigid body carries `pos`/`yaw` — which is why an editor move
 * is a 0.2 ms re-pose and a scale edit is a rebuild (`physics/rapierService.ts`).
 * Every fit below (`fitBox`/`fitSphere`/`fitCapsule`/`bakeTrimesh`/`bakeHull`) takes the node's
 * local points plus its matrix RELATIVE TO THE ASSET ROOT, so the authored TRS of a `UBX_` node is
 * a real OBB, not an axis-aligned guess.
 *
 * ⚠️ **AND THE TRANSFORM IS NO LONGER TOTAL.** Under a NON-UNIFORM scale a hull and a trimesh are
 * still exact (an affine map of a convex hull is the hull of the mapped points; a triangle soup
 * maps vertex-wise), a box is exact iff its scaled basis is still orthogonal — but a `USP_` sphere
 * and a `UCP_` capsule become ELLIPSOIDS, which Rapier has no shape for. So `transformShapes`
 * returns `{ shapes, refusals }`: the shape it cannot represent is DROPPED and named, never
 * silently approximated by its largest axis (an invisible collider that does not match the mesh is
 * the exact failure this file's header opens with).
 */

/** A world-data triple (`pos`, `size` — the same shape `world.json` carries). */
export type Vec3 = readonly [number, number, number];
/** `x, y, z, w` — three's and Rapier's order. */
export type Quat = readonly [number, number, number, number];

/** ONE regex; the future cook shares it. Case-insensitive: Blender exports `ucx_` too. */
export const COLLISION_PREFIX = /^(UCX|UBX|USP|UCP)_(.+?)(?:_(\d+))?$/i;

export type CollisionPrefix = 'UCX' | 'UBX' | 'USP' | 'UCP';

/**
 * What an ASSET declares. `derived` (a runtime V-HACD — `ColliderDesc.convexDecomposition` exists
 * in rapier 0.20) is RESERVED: hollow architecture still wants trimesh, and a mode nobody has
 * measured must not be a word an artist can pick.
 */
export type AssetCollisionMode = 'none' | 'ucx' | 'trimesh' | 'box';
export const ASSET_COLLISION_MODES: readonly AssetCollisionMode[] = ['none', 'ucx', 'trimesh', 'box'];
/** What a PLACEMENT declares: `asset` defers to the manifest, the rest override it. */
export type PlacementCollisionMode = 'asset' | 'trimesh' | 'box' | 'none';
export const PLACEMENT_COLLISION_MODES: readonly PlacementCollisionMode[] = ['asset', 'trimesh', 'box', 'none'];

export type CollisionShape =
  /** `UCX_` → `ColliderDesc.convexHull(points)` (null from Rapier ⇒ warn once, skip). */
  | { kind: 'hull'; points: Float32Array }
  /** `UBX_` → `cuboid(half) + setTranslation(center) + setRotation(quat)` — a real OBB. */
  | { kind: 'box'; center: Vec3; half: Vec3; quat: Quat }
  /** `USP_` → `ball(r)` at `center`. */
  | { kind: 'sphere'; center: Vec3; r: number }
  /** `UCP_` → `capsule` posed from the segment `a → b`. */
  | { kind: 'capsule'; a: Vec3; b: Vec3; r: number }
  /** One render primitive, exact. */
  | { kind: 'trimesh'; vertices: Float32Array; indices: Uint32Array };

export type CollisionFrom = 'placement' | 'asset' | 'default';

/**
 * ⭐ WHERE THE SHAPES CAME FROM (L1.0/L2) — `cooked` = deserialised from a `<assetId>.col` sidecar,
 * `runtime` = walked out of the parsed GLB at load.
 *
 * It is DELIBERATELY NOT folded into `CollisionFrom`. That one answers *which declaration chose the
 * mode* and is what `describeCollisionSource` says; this one answers *where the geometry came
 * from*. Merging them is how "From asset" starts meaning two different things, and Details shows
 * the two on two lines for exactly that reason.
 */
export type CollisionOrigin = 'cooked' | 'runtime';

/**
 * Why a cooked sidecar was NOT used. `absent` is the ORDINARY case and is not a fault — there is no
 * cook for most worlds, and `probeLoad.ts:62` already says it in those words. Every other value is
 * a sentence an artist should read; the runtime walk answers all of them identically, so a refusal
 * costs nothing but the milliseconds we had before the sidecar existed. That is why this list may
 * grow freely and why no arm of it is grave.
 *
 * — `absent`     — no URL for this asset, a 404, or an empty body.
 * — `unreadable` — the fetch itself threw (offline, aborted, CORS).
 * — `format`     — `decodeCollisionFile` refused it (magic, version, either hash, section bounds).
 * — `bytes`      — the header describes a GLB of a different length: the wrong file entirely.
 * — `hash`       — same length, different bytes: an EDITED GLB. The silent one.
 * — `cooker`     — `COOKER_VERSION` moved: the walker's own arithmetic changed under the file.
 * — `census`     — the parsed scene has a different primitive census than the cook saw.
 * — `mode`       — the resolved mode is not the one the file carries (a placement override, or an
 *                  asset declaration edited since the cook).
 */
export type ColRefusal =
  | 'absent'
  | 'unreadable'
  | 'format'
  | 'bytes'
  | 'hash'
  | 'cooker'
  | 'census'
  | 'mode';

/** What an entity PUBLISHES (`EntityInstance.collision`) once its content has arrived. */
export interface CollisionSource {
  mode: AssetCollisionMode;
  from: CollisionFrom;
  /** In ASSET space (the GLB root's frame) — unscaled, unposed. The binder applies the placement. */
  shapes: CollisionShape[];
  /** Triangles across every trimesh shape (0 for hulls/boxes — they are not priced by triangle). */
  tris: number;
  /**
   * Wall-clock the extraction took, ms (the entity measures; this module never reads a clock). For
   * a COOKED source it is the fetch-decode-verify time, so `buildMs` stays comparable across both
   * paths on the same field — which is the whole point of publishing `origin` beside it.
   *
   * ⚠️ `extractMs === walkMs + yieldMs`, BY CONSTRUCTION (`walkMs` is DERIVED by subtraction, never
   * measured separately), because one number with three causes is how "the extraction takes 2.9 s"
   * survived: 2,715 ms of that 2,923 ms was 102 round trips through a load frame, and nothing in
   * the report could say so. `tests/staticMesh.test.ts` asserts the identity.
   */
  extractMs: number;
  /** Arithmetic: composing matrices and baking vertex arrays. The part a COOK can delete. */
  walkMs: number;
  /** Waiting: time spent inside `yieldFrame()` between primitives. The part a BUDGET can delete. */
  yieldMs: number;
  /** L2: `cooked` when a sidecar answered, `runtime` when the GLB was walked. */
  origin: CollisionOrigin;
  /** L2: why the sidecar was refused, when there was one to refuse. `null` when it was used. */
  refused: ColRefusal | null;
  /** Verbatim sentences an artist reads in Details — a forgotten hull, a degenerate box. */
  warnings: string[];
}

/** What the physics service REPORTS per bound placement — the Details line and `__fwg.state()`. */
export interface EntityCollisionInfo {
  index: number;
  /** The asset id (the binder's label), '' when unknown. */
  asset: string;
  mode: AssetCollisionMode;
  from: CollisionFrom;
  /** Shapes actually bound as colliders (a degenerate hull Rapier refused is not counted). */
  shapes: number;
  tris: number;
  /** Colliders in the physics world for this placement. */
  colliders: number;
  /**
   * `extractMs + rapierMs` — the whole cost of making this placement solid. Its identity is
   * UNCHANGED since S5, so every existing reader still means what it meant; the three numbers below
   * are what it decomposes into, published rather than folded away because one number with three
   * causes cannot be optimised on evidence.
   */
  buildMs: number;
  /** `CollisionSource.walkMs` — the arithmetic half of the extraction (0 for a cooked source). */
  walkMs: number;
  /** `CollisionSource.yieldMs` — the WAITING half of the extraction (0 for a cooked source). */
  yieldMs: number;
  /** Rapier's own build: `transformShapes` + N `createCollider` calls. Unmoved by any cook. */
  rapierMs: number;
  /** L2: where the shapes came from. `__fwg.state().collision.entities[i].origin`. */
  origin: CollisionOrigin;
  /** L2: why a cooked sidecar was refused, when there was one. `null` when used, or when absent. */
  refused: ColRefusal | null;
  warnings: string[];
}

/**
 * The placement half of the D60 transform, as a plain record (`placementPose` derives it).
 *
 * `scale` is ALWAYS a triple here, whichever way the file spelled it — one representation past the
 * schema, so nothing downstream branches on the type of `def.scale`. Uniform is `[s, s, s]`.
 */
export interface PlacementPose {
  pos: Vec3;
  yawDeg: number;
  /** Pitch, degrees (F1). 0 for every placement authored before rotation was widened. */
  pitchDeg: number;
  /** Roll, degrees (F1). */
  rollDeg: number;
  scale: Vec3;
}

/**
 * THE resolver. Precedence: an explicit placement override → the asset's declaration → the
 * default. Absent everywhere ⇒ `trimesh` (B2 — Sponza gets collision with ZERO bytes changed in
 * its `world.json`, so its baked atlas keeps its `worldHash`). An unknown asset (the entity will
 * show a placeholder) also resolves to the default; the loader has nothing to extract from and
 * says so in its own warning.
 */
export function resolveCollisionMode(
  asset: { kind: string; collision?: AssetCollisionMode | undefined } | undefined,
  placement: { collision?: PlacementCollisionMode | undefined },
): { mode: AssetCollisionMode; from: CollisionFrom } {
  const p = placement.collision;
  if (p === 'trimesh' || p === 'box' || p === 'none') return { mode: p, from: 'placement' };
  const a = asset?.collision;
  if (a !== undefined && (ASSET_COLLISION_MODES as readonly string[]).includes(a)) return { mode: a, from: 'asset' };
  return { mode: asset === undefined || asset.kind === 'mesh' ? 'trimesh' : 'none', from: 'default' };
}

/** `UCX_Crate_01` → `{ prefix:'UCX', base:'Crate', ordinal:1 }`; anything else → null. */
export function parseCollisionNodeName(
  name: string,
): { prefix: CollisionPrefix; base: string; ordinal: number | null } | null {
  const m = COLLISION_PREFIX.exec(name);
  if (!m) return null;
  return {
    prefix: m[1]!.toUpperCase() as CollisionPrefix,
    base: m[2]!,
    ordinal: m[3] === undefined ? null : Number(m[3]),
  };
}

const num = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);

/** The placement's pose from a RAW def — never throws; missing fields take the schema's defaults. */
export function placementPose(def: unknown): PlacementPose {
  const d = (typeof def === 'object' && def !== null ? def : {}) as Record<string, unknown>;
  const p = d['pos'];
  const pos: Vec3 =
    Array.isArray(p) && p.length >= 3 ? [num(p[0]), num(p[1]), num(p[2])] : [0, 0, 0];
  return {
    pos,
    yawDeg: num(d['yawDeg']),
    // F1: absent ⇒ 0, which is what every pre-rotation world means and what the schema's
    // `.default(0)` materialises. Lenient like the rest of this function — it reads a RAW
    // placement, so a garbage value must land the mesh unrotated rather than throw a boot.
    pitchDeg: num(d['pitchDeg']),
    rollDeg: num(d['rollDeg']),
    scale: scaleVec3(d['scale']),
  };
}

/**
 * ⭐ THE ONE FOLD OF THE TWO SPELLINGS OF `scale` — a number (uniform) or `[x, y, z]` — onto the
 * `[x, y, z]` everything downstream uses. Lenient like `placementPose` and for the same reason:
 * it reads the RAW placement (`WorldSchema.entities` is `z.array(z.unknown())`, so nothing has
 * validated it yet) and a bad value must land the mesh at its authored size rather than throw a
 * boot. A non-finite or non-positive component falls back to 1: a 0 or a negative would be an
 * invisible mesh or an inside-out one whose colliders have flipped winding, and neither reads as
 * "the artist meant that".
 */
export function scaleVec3(v: unknown): Vec3 {
  /*
   * ⭐ F2 — A NEGATIVE COMPONENT IS A MIRROR, AND IT IS NOW CARRIED, NOT DISCARDED.
   *
   * This used to read `n > 0 ? n : 1`, on the reasoning that "a 0 or a negative would be an
   * invisible mesh or an inside-out one". Zero still is, and is still refused. A negative is NOT:
   * it is how every DCC tool and every level editor spells a mirrored instance, and **633 of
   * Mars's 5,564 placements carry one.** Clamping them to 1 does not fail — it silently un-mirrors
   * an eighth of the level, which is precisely the "renders fine, is wrong" class.
   *
   * ⚠️ 633 AND 627 ARE BOTH RIGHT AND THEY ARE DIFFERENT QUESTIONS, so quote the one you mean.
   * 633 placements carry a negative COMPONENT; **627** have a negative DETERMINANT (an odd number
   * of them) and are therefore genuinely mirrored, with flipped winding. The other six carry two
   * negatives, which is a 180° rotation and no mirror at all. Rendering rule 3's `frontFace` fork
   * is the 627; "does this placement need its sign carried" is the 633.
   *
   * ⚠️ The known cost is rendering rule 3: a negative determinant flips `frontFace` and forks a
   * `GPURenderPipeline`, so mirrored meshes draw on a second pipeline. That is a MEASURABLE cost,
   * and the owner's standing ruling is that a measurable cost does not get to veto a correctness
   * requirement on a prediction. Baking mirrored variants at cook time remains the escape hatch if
   * measurement asks for it; it is not a reason to lose the information on the way in.
   *
   * Physics is unaffected either way: an affine map of a convex hull is still the hull of the
   * mapped points, and a trimesh is a soup with no winding contract. `transformShapes` already
   * refuses the shapes a non-uniform scale genuinely cannot express (spheres, capsules).
   */
  const one = (x: unknown): number => {
    const n = num(x, 1);
    return Number.isFinite(n) && n !== 0 ? n : 1;
  };
  if (Array.isArray(v)) return [one(v[0]), one(v[1]), one(v[2])];
  const s = one(v);
  return [s, s, s];
}

/** True when all three components are equal — the cheap arm of every shape decision below. */
export const isUniformScale = (s: Vec3): boolean => s[0] === s[1] && s[1] === s[2];

/**
 * Component-wise equality of two scales. It exists because `scale` became an ARRAY: every `===`
 * that used to compare two numbers now compares two references and is false for `[2,2,2]` against
 * `[2,2,2]` — which in `poseEntityCollision` would rebuild 103 trimeshes on every pointer move.
 */
export const sameScale = (a: Vec3, b: Vec3): boolean => a[0] === b[0] && a[1] === b[1] && a[2] === b[2];

// ── Quaternion / matrix arithmetic (tiny, local — `three` may not enter this file) ──────────────

/** Rotation about +Y by `deg`, three's convention (`Object3D.rotation.y`). */
export function yawQuat(deg: number): Quat {
  const h = (deg * Math.PI) / 360;
  return [0, Math.sin(h), 0, Math.cos(h)];
}

/** Rotation about +X by `deg` (pitch). */
export function pitchQuat(deg: number): Quat {
  const h = (deg * Math.PI) / 360;
  return [Math.sin(h), 0, 0, Math.cos(h)];
}

/** Rotation about +Z by `deg` (roll). */
export function rollQuat(deg: number): Quat {
  const h = (deg * Math.PI) / 360;
  return [0, 0, Math.sin(h), Math.cos(h)];
}

/**
 * ⭐ THE PLACEMENT'S FULL ROTATION — ONE COMPOSITION, IN ONE PLACE (F1).
 *
 * `Ry(yaw) ⊗ Rx(pitch) ⊗ Rz(roll)` — roll applied first, then pitch, then yaw. That is the
 * standard intrinsic yaw-pitch-roll, and it is **exactly three's Euler order `'YXZ'`**, which is
 * what lets `entities/staticMesh.ts` write `rotation.set(pitch, yaw, roll, 'YXZ')` and get a
 * quaternion identical to this one. The two must agree or the collider sits at a different angle
 * from the mesh it is supposed to be — a failure that renders correctly and walks wrong, which is
 * the worst kind this repo keeps finding.
 *
 * ⚠️ **THIS IS GAME SPACE, NOT UE SPACE, AND THAT SEPARATION IS DELIBERATE.** UE's rotator has its
 * own axes (pitch about UE's +Y, yaw about +Z, roll about +X), its own composition order, and its
 * own handedness. Converting one to the other is a real derivation with a plausible wrong answer,
 * and it belongs in the level EMIT — once, tested against UE's own exported node transforms — not
 * smuggled in here where every runtime consumer would inherit an assumption nobody checked. By the
 * time a pose reaches this function the numbers are already ours.
 *
 * `yawQuat` is deliberately still exported and still used where only yaw exists: identical output
 * for the pitch=roll=0 case, and it keeps the hot re-pose path free of two multiplies.
 */
export function placementQuat(pose: Pick<PlacementPose, 'yawDeg' | 'pitchDeg' | 'rollDeg'>): Quat {
  if (pose.pitchDeg === 0 && pose.rollDeg === 0) return yawQuat(pose.yawDeg);
  return quatMul(quatMul(yawQuat(pose.yawDeg), pitchQuat(pose.pitchDeg)), rollQuat(pose.rollDeg));
}

/** True when two poses' rotations are equal component-wise — the `sameScale` lesson, for angles. */
export const sameRotation = (
  a: Pick<PlacementPose, 'yawDeg' | 'pitchDeg' | 'rollDeg'>,
  b: Pick<PlacementPose, 'yawDeg' | 'pitchDeg' | 'rollDeg'>,
): boolean => a.yawDeg === b.yawDeg && a.pitchDeg === b.pitchDeg && a.rollDeg === b.rollDeg;

/** Hamilton product `a ⊗ b` — apply `b`, then `a`. */
export function quatMul(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

/** Rotate `v` by unit quaternion `q`. */
export function quatRotate(q: Quat, v: Vec3): Vec3 {
  const [qx, qy, qz, qw] = q;
  const [x, y, z] = v;
  // t = 2 · (q.xyz × v); v' = v + w·t + q.xyz × t
  const tx = 2 * (qy * z - qz * y);
  const ty = 2 * (qz * x - qx * z);
  const tz = 2 * (qx * y - qy * x);
  return [
    x + qw * tx + (qy * tz - qz * ty),
    y + qw * ty + (qz * tx - qx * tz),
    z + qw * tz + (qx * ty - qy * tx),
  ];
}

/** A column-major 4×4 (three's `Matrix4.elements` layout) applied to a point. */
export function matrixApply(m: ArrayLike<number>, p: Vec3): Vec3 {
  const [x, y, z] = p;
  return [
    m[0]! * x + m[4]! * y + m[8]! * z + m[12]!,
    m[1]! * x + m[5]! * y + m[9]! * z + m[13]!,
    m[2]! * x + m[6]! * y + m[10]! * z + m[14]!,
  ];
}

/** The per-axis scale of a column-major 4×4 (column lengths). */
export function matrixScale(m: ArrayLike<number>): Vec3 {
  return [
    Math.hypot(m[0]!, m[1]!, m[2]!),
    Math.hypot(m[4]!, m[5]!, m[6]!),
    Math.hypot(m[8]!, m[9]!, m[10]!),
  ];
}

/** The rotation of a column-major 4×4 as a unit quaternion (scale divided out; three's `decompose`). */
export function matrixQuat(m: ArrayLike<number>): Quat {
  const [sx, sy, sz] = matrixScale(m);
  const ix = sx === 0 ? 1 : 1 / sx;
  const iy = sy === 0 ? 1 : 1 / sy;
  const iz = sz === 0 ? 1 : 1 / sz;
  // Row-major 3×3 from the normalised columns.
  const m00 = m[0]! * ix;
  const m01 = m[4]! * iy;
  const m02 = m[8]! * iz;
  const m10 = m[1]! * ix;
  const m11 = m[5]! * iy;
  const m12 = m[9]! * iz;
  const m20 = m[2]! * ix;
  const m21 = m[6]! * iy;
  const m22 = m[10]! * iz;
  const trace = m00 + m11 + m22;
  let q: [number, number, number, number];
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1);
    q = [(m21 - m12) * s, (m02 - m20) * s, (m10 - m01) * s, 0.25 / s];
  } else if (m00 > m11 && m00 > m22) {
    const s = 2 * Math.sqrt(1 + m00 - m11 - m22);
    q = [0.25 * s, (m01 + m10) / s, (m02 + m20) / s, (m21 - m12) / s];
  } else if (m11 > m22) {
    const s = 2 * Math.sqrt(1 + m11 - m00 - m22);
    q = [(m01 + m10) / s, 0.25 * s, (m12 + m21) / s, (m02 - m20) / s];
  } else {
    const s = 2 * Math.sqrt(1 + m22 - m00 - m11);
    q = [(m02 + m20) / s, (m12 + m21) / s, 0.25 * s, (m10 - m01) / s];
  }
  const n = Math.hypot(q[0], q[1], q[2], q[3]) || 1;
  return [q[0] / n, q[1] / n, q[2] / n, q[3] / n];
}

// ── Fits over a node's LOCAL points + its matrix relative to the asset root ────────────────────

/** Axis-aligned bounds of an xyz-triple array. Null for an empty array. */
export function aabbOf(points: ArrayLike<number>): { lo: Vec3; hi: Vec3 } | null {
  if (points.length < 3) return null;
  const lo = [Infinity, Infinity, Infinity];
  const hi = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i + 2 < points.length; i += 3) {
    for (let k = 0; k < 3; k++) {
      const v = points[i + k]!;
      if (v < lo[k]!) lo[k] = v;
      if (v > hi[k]!) hi[k] = v;
    }
  }
  return { lo: [lo[0]!, lo[1]!, lo[2]!], hi: [hi[0]!, hi[1]!, hi[2]!] };
}

const IDENTITY_QUAT: Quat = [0, 0, 0, 1];

const centerOf = (b: { lo: Vec3; hi: Vec3 }): Vec3 => [
  (b.lo[0] + b.hi[0]) / 2,
  (b.lo[1] + b.hi[1]) / 2,
  (b.lo[2] + b.hi[2]) / 2,
];

const scaledHalf = (b: { lo: Vec3; hi: Vec3 }, s: Vec3): Vec3 => [
  ((b.hi[0] - b.lo[0]) / 2) * s[0],
  ((b.hi[1] - b.lo[1]) / 2) * s[1],
  ((b.hi[2] - b.lo[2]) / 2) * s[2],
];

/** An axis-aligned box shape from bounds (the `box` fallback: the asset's AABB). */
export function boxFromAabb(lo: Vec3, hi: Vec3): CollisionShape {
  const b = { lo, hi };
  return { kind: 'box', center: centerOf(b), half: scaledHalf(b, [1, 1, 1]), quat: IDENTITY_QUAT };
}

/** `UBX_`: the node's local AABB carried through its TRS — an oriented box. */
export function fitBox(local: ArrayLike<number>, m: ArrayLike<number>): CollisionShape | null {
  const b = aabbOf(local);
  if (!b) return null;
  return { kind: 'box', center: matrixApply(m, centerOf(b)), half: scaledHalf(b, matrixScale(m)), quat: matrixQuat(m) };
}

/** `USP_`: centre through the TRS, radius = the largest scaled half-extent (a sphere mesh has three equal). */
export function fitSphere(local: ArrayLike<number>, m: ArrayLike<number>): CollisionShape | null {
  const b = aabbOf(local);
  if (!b) return null;
  const h = scaledHalf(b, matrixScale(m));
  return { kind: 'sphere', center: matrixApply(m, centerOf(b)), r: Math.max(h[0], h[1], h[2]) };
}

/**
 * `UCP_`: the long axis of the local AABB is the capsule axis; the radius is the mean of the two
 * short half-extents; the segment is the long half-extent minus the radius (a capsule authored as
 * a capsule mesh round-trips; a plain cylinder gets its ends rounded inward, which is the
 * conservative side).
 */
export function fitCapsule(local: ArrayLike<number>, m: ArrayLike<number>): CollisionShape | null {
  const b = aabbOf(local);
  if (!b) return null;
  const s = matrixScale(m);
  const half = scaledHalf(b, s);
  let axis = 0;
  for (let k = 1; k < 3; k++) if (half[k]! > half[axis]!) axis = k;
  const others = [0, 1, 2].filter((k) => k !== axis);
  const r = (half[others[0]!]! + half[others[1]!]!) / 2;
  const scaleAxis = s[axis]! === 0 ? 1 : s[axis]!;
  const localHalfLen = Math.max((half[axis]! - r) / scaleAxis, 0);
  const c = centerOf(b);
  const a = [...c] as [number, number, number];
  const e = [...c] as [number, number, number];
  a[axis] = c[axis]! - localHalfLen;
  e[axis] = c[axis]! + localHalfLen;
  return { kind: 'capsule', a: matrixApply(m, a), b: matrixApply(m, e), r };
}

/** Every local point through the TRS — the hull's input, and the trimesh's vertex array. */
function bakePoints(local: ArrayLike<number>, m: ArrayLike<number>): Float32Array {
  const out = new Float32Array(local.length - (local.length % 3));
  for (let i = 0; i + 2 < local.length; i += 3) {
    const p = matrixApply(m, [local[i]!, local[i + 1]!, local[i + 2]!]);
    out[i] = p[0];
    out[i + 1] = p[1];
    out[i + 2] = p[2];
  }
  return out;
}

/** `UCX_`: every local point through the TRS; Rapier's `convexHull` does the hull. */
export function bakeHull(local: ArrayLike<number>, m: ArrayLike<number>): CollisionShape {
  return { kind: 'hull', points: bakePoints(local, m) };
}

/** One render primitive → an exact trimesh in asset space (an unindexed primitive is numbered). */
export function bakeTrimesh(
  local: ArrayLike<number>,
  indices: ArrayLike<number> | null,
  m: ArrayLike<number>,
): CollisionShape {
  const vertices = bakePoints(local, m);
  let idx: Uint32Array;
  if (indices) {
    idx = new Uint32Array(indices.length - (indices.length % 3));
    for (let i = 0; i < idx.length; i++) idx[i] = indices[i]!;
  } else {
    const n = vertices.length / 3;
    idx = new Uint32Array(n - (n % 3));
    for (let i = 0; i < idx.length; i++) idx[i] = i;
  }
  return { kind: 'trimesh', vertices, indices: idx };
}

/** Triangles across a shape list (trimeshes only — hulls/boxes are not priced by triangle). */
export function trisOf(shapes: readonly CollisionShape[]): number {
  let n = 0;
  for (const s of shapes) if (s.kind === 'trimesh') n += Math.floor(s.indices.length / 3);
  return n;
}

// ── ⭐ THE ONE WALK, OVER A STRUCTURAL GRAPH (L1.A) ─────────────────────────────────────────────

/**
 * Column-major 4×4 product `a · b` (apply `b`, then `a`) — three's `Matrix4.multiply` layout, and
 * the one composition {@link collectCollisionShapes} walks a graph with. `three` may not enter this
 * file and the cook's graph library has its own matrix type, so the arithmetic lives HERE or it
 * lives twice.
 */
export function matrixMultiply(a: ArrayLike<number>, b: ArrayLike<number>): number[] {
  const out = new Array<number>(16);
  for (let col = 0; col < 4; col++) {
    for (let row = 0; row < 4; row++) {
      out[col * 4 + row] =
        a[row]! * b[col * 4]! +
        a[4 + row]! * b[col * 4 + 1]! +
        a[8 + row]! * b[col * 4 + 2]! +
        a[12 + row]! * b[col * 4 + 3]!;
    }
  }
  return out;
}

/** The identity, column-major — what a walk starts from and what a synthetic root carries. */
export const IDENTITY_MATRIX: readonly number[] = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

/**
 * One RENDER PRIMITIVE's geometry, read on demand.
 *
 * ⚠️ `positions` IS A THUNK, AND THAT IS LOAD-BEARING. Materialising 103 primitives' vertex arrays
 * in one synchronous turn (192,496 × 3 floats on Sponza) is exactly the stall `extractCollision` is
 * async to avoid, and the cook reads its accessors element-wise for the same reason. The walker
 * asks a primitive for its points only when it is about to bake them — so the `geometry: false`
 * arm (the runtime's strip pass) never touches a rendered primitive's vertices at all.
 */
export interface CollisionPrim {
  /**
   * xyz triples in the node's LOCAL frame, DENORMALISED. ⚠️ A `KHR_mesh_quantization` accessor
   * hands back raw shorts through its own array: the cook adapter must read it through
   * `Accessor.getElement`, or the collider is silently built at 1/32767 scale.
   */
  positions(): ArrayLike<number>;
  /** Triangle indices, or `null` for an unindexed primitive ({@link bakeTrimesh} numbers those). */
  indices(): ArrayLike<number> | null;
}

/**
 * A node in SOME graph — three's `Object3D` tree at load, gltf-transform's `Node` tree in the cook.
 * `T` is the HOST node type purely so the walker can hand back the nodes the caller must remove
 * from its own graph; the walker never touches a `T`.
 */
export interface CollisionNode<T = unknown> {
  ref: T;
  name: string;
  /**
   * Column-major 16, LOCAL to the parent. The walker composes down; nobody hands it a world matrix.
   * ⚠️ The ROOT's own matrix is IGNORED — "asset space" IS the root's frame, which is the frame
   * `CollisionSource.shapes` is documented in and the frame the binder poses from.
   */
  localMatrix: ArrayLike<number>;
  /** One entry per RENDER PRIMITIVE on this node (three's loader emits one `Mesh` per primitive). */
  prims: readonly CollisionPrim[];
  children: readonly CollisionNode<T>[];
}

/** What one walk produces. Every list is in DOCUMENT ORDER (pre-order DFS, children in order). */
export interface CollisionWalk<T> {
  /** `UCX_/UBX_/USP_/UCP_` shapes, topmost-prefixed-ancestor rule, asset space. */
  authored: CollisionShape[];
  /** Those nodes' names, in document order — the `census.authored` column, `[]` when none. */
  authoredNames: string[];
  /** The nodes the render graph must lose — `ref` is the CALLER's own node, never ours. */
  strip: T[];
  /** One shape per render primitive OUTSIDE an authored subtree, asset space (the `trimesh` mode). */
  trimesh: CollisionShape[];
  /**
   * The AABB of everything `trimesh` covers, in asset space — free while walking, and the answer to
   * a `box` placement. `null` when nothing was walked (a hulls-only GLB, or `geometry: false`).
   */
  aabb: { lo: Vec3; hi: Vec3 } | null;
  /**
   * EVERY glTF render primitive in the tree, authored subtrees INCLUDED and degenerates INCLUDED —
   * i.e. the PRE-STRIP, PRE-SKIP census. Stated that way because it is the one number a cooked
   * sidecar and a runtime scene can both produce: the runtime has already stripped and skipped by
   * the time anyone asks, so a post-strip count could only ever be compared against itself.
   */
  primitives: number;
  /** Primitives with fewer than 9 position floats — no triangle, skipped by both sides. */
  skipped: number;
  /** Vertices across `trimesh` (position triples), and index elements across it. */
  positions: number;
  indices: number;
  warnings: string[];
}

/**
 * ⭐ THE COOK'S OWN ARITHMETIC VERSION. Bump it when the SAME input must yield DIFFERENT numbers —
 * a fix to `fitCapsule`'s axis choice, to `matrixQuat`'s trace branch, to the composition order
 * here. It is written into every sidecar's header and checked when one is read, because that is
 * the ONE edit class a hash of the source GLB is structurally blind to: the bytes are identical
 * and their INTERPRETATION moved.
 */
export const COOKER_VERSION = 1;

/**
 * ⭐ ONE WALK, TWO ADAPTERS — the whole reason this function exists.
 *
 * "What is a `UCX_` hull" had exactly one definition and it lived inside an entity module the cook
 * may not import (`tests/entityBoundary.test.ts`, and `tools/bundler/*` must not import `three`).
 * A second copy in the cook is the failure the cook exists to prevent: a sidecar whose hulls sit a
 * yaw off the mesh they guard reports nothing at all — the capsule simply passes through.
 *
 * The prefixed-node rule is the TOPMOST prefixed ancestor: an exporter may wrap a multi-primitive
 * hull in a group, and a prefixed node nested under a prefixed node is one shape, not two.
 *
 * `geometry: false` (the runtime's strip pass) skips the rendered primitives entirely: `trimesh`
 * comes back empty and `aabb` null, and not one rendered vertex array is materialised. The cook
 * and the `box` fallback pass the default.
 */
export function collectCollisionShapes<T>(
  root: CollisionNode<T>,
  opts: { geometry?: boolean } = {},
): CollisionWalk<T> {
  const geometry = opts.geometry !== false;
  const authored: CollisionShape[] = [];
  const authoredNames: string[] = [];
  const strip: T[] = [];
  const trimesh: CollisionShape[] = [];
  const warnings: string[] = [];
  let primitives = 0;
  let skipped = 0;
  let positions = 0;
  let indices = 0;
  let lo: [number, number, number] | null = null;
  let hi: [number, number, number] | null = null;

  const grow = (b: { lo: Vec3; hi: Vec3 }): void => {
    if (lo === null || hi === null) {
      lo = [b.lo[0], b.lo[1], b.lo[2]];
      hi = [b.hi[0], b.hi[1], b.hi[2]];
      return;
    }
    for (let k = 0; k < 3; k++) {
      if (b.lo[k]! < lo[k]!) lo[k] = b.lo[k]!;
      if (b.hi[k]! > hi[k]!) hi[k] = b.hi[k]!;
    }
  };

  /** `m` ALREADY carries this node's own TRS. Everything below a prefixed node is that one shape. */
  const collectAuthored = (
    node: CollisionNode<T>,
    m: ArrayLike<number>,
    prefix: CollisionPrefix,
    top: string,
  ): void => {
    for (const prim of node.prims) {
      primitives++;
      const local = prim.positions();
      let shape: CollisionShape | null;
      if (prefix === 'UCX') shape = local.length >= 12 ? bakeHull(local, m) : null;
      else if (prefix === 'UBX') shape = fitBox(local, m);
      else if (prefix === 'USP') shape = fitSphere(local, m);
      else shape = fitCapsule(local, m);
      if (shape === null) warnings.push(`collision node '${top}' has no usable vertices — skipped`);
      else authored.push(shape);
    }
    for (const child of node.children) {
      collectAuthored(child, matrixMultiply(m, child.localMatrix), prefix, top);
    }
  };

  const visit = (node: CollisionNode<T>, parentMatrix: ArrayLike<number>, isRoot: boolean): void => {
    const m = isRoot ? parentMatrix : matrixMultiply(parentMatrix, node.localMatrix);
    if (!isRoot) {
      const parsed = parseCollisionNodeName(node.name);
      if (parsed !== null) {
        authoredNames.push(node.name);
        strip.push(node.ref);
        collectAuthored(node, m, parsed.prefix, node.name);
        return; // the subtree belongs to this shape; it is never drawn and never a trimesh
      }
    }
    for (const prim of node.prims) {
      primitives++;
      if (!geometry) continue;
      const local = prim.positions();
      if (local.length < 9) {
        skipped++;
        continue;
      }
      const shape = bakeTrimesh(local, prim.indices(), m);
      if (shape.kind !== 'trimesh') continue; // unreachable — narrows the union for the counters
      trimesh.push(shape);
      positions += shape.vertices.length / 3;
      indices += shape.indices.length;
      const b = aabbOf(shape.vertices);
      if (b !== null) grow(b);
    }
    for (const child of node.children) visit(child, m, false);
  };

  visit(root, IDENTITY_MATRIX, true);
  return {
    authored,
    authoredNames,
    strip,
    trimesh,
    aabb: lo !== null && hi !== null ? { lo, hi } : null,
    primitives,
    skipped,
    positions,
    indices,
    warnings,
  };
}

// ── The similarity transform ───────────────────────────────────────────────────────────────────

/**
 * The verbatim refusal a shape earns when a non-uniform scale cannot be expressed in its kind.
 * It reaches the artist through `EntityCollisionInfo.warnings`, which the Details panel prints as
 * notes, so the sentence names the fix and not just the fault.
 */
export const NON_UNIFORM_REFUSAL = (kind: string, s: Vec3): string =>
  `a ${kind} collider cannot take the non-uniform scale [${s[0]}, ${s[1]}, ${s[2]}] — skipped ` +
  `(use a uniform scale, or a UCX_ hull / trimesh collider, which are exact under any scale)`;

/** What `transformShapes` produced, and what it could not. */
export interface TransformedShapes {
  shapes: CollisionShape[];
  /** One verbatim sentence per DROPPED shape. Empty on every uniform scale. */
  refusals: string[];
}

/**
 * Asset-space shapes → the frame of a placement (`p' = R(yaw, pitch, roll) · (S · p) + pos`, `S`
 * diagonal). Pure, and the arithmetic the binder uses with `pos = 0` and no rotation (scale only —
 * the rigid body carries the rest) and the overlay uses whole. A quaternion composes as `R ⊗ q`.
 * F1: `R` is `placementQuat`, which is yaw alone whenever pitch and roll are 0.
 *
 * ⭐ **IT RETURNS ITS REFUSALS BESIDE ITS SHAPES, so a caller cannot take the geometry and forget
 * the reasons.** Under a non-uniform `S`:
 *
 *  · `hull` / `trimesh` — EXACT. `S` is affine, so the image of a convex hull is the hull of the
 *    imaged points, and a triangle soup maps vertex by vertex. Winding survives because every
 *    component is positive (`scaleVec3`), i.e. `det(S) > 0`.
 *  · `box` — exact IFF the scaled basis is still orthogonal. The world box is
 *    `R·S·c + R·S·Q·(h∘u)`, and `R` is a rotation, so box-ness is decided by `S·Q` alone: the
 *    three columns `S·q_i` must stay mutually perpendicular. They always do for an axis-aligned
 *    box (the common case: `mode: 'box'` is the asset AABB, and a `UBX_` node exported without a
 *    rotation) AND for any `Q` that permutes axes; a 45°-yawed `UBX_` under `[2,1,1]` becomes a
 *    parallelepiped and is refused. When it holds, the new half-extents are `h_i·|S·q_i|` and the
 *    new basis is those columns normalised.
 *  · `sphere` / `capsule` — IMPOSSIBLE. Both are swept spheres; a non-uniform `S` makes them swept
 *    ELLIPSOIDS, and Rapier 0.20 has no such shape. Refused, never approximated: the two honest
 *    approximations (the max axis, the mean) are respectively a collider bigger than the mesh, so
 *    the player bumps into mid-air, and one smaller, so he sinks into it — and both look correct
 *    in every screenshot.
 *
 * A refused shape is DROPPED and the others still bind — the same rule 6 shape as a degenerate
 * `UCX_` hull (`rapierService.setEntityCollision`).
 */
export function transformShapes(shapes: readonly CollisionShape[], pose: PlacementPose): TransformedShapes {
  // F1: the FULL placement rotation, not just yaw — `placementQuat` short-circuits to `yawQuat`
  // when pitch and roll are 0, so every pre-rotation world produces a bit-identical quaternion and
  // no existing collider moves.
  const q = placementQuat(pose);
  const s = pose.scale;
  const uniform = isUniformScale(s);
  const [px, py, pz] = pose.pos;
  const pt = (v: Vec3): Vec3 => {
    const r = quatRotate(q, [v[0] * s[0], v[1] * s[1], v[2] * s[2]]);
    return [r[0] + px, r[1] + py, r[2] + pz];
  };
  const arr = (a: Float32Array): Float32Array => {
    const out = new Float32Array(a.length);
    for (let i = 0; i + 2 < a.length; i += 3) {
      const p = pt([a[i]!, a[i + 1]!, a[i + 2]!]);
      out[i] = p[0];
      out[i + 1] = p[1];
      out[i + 2] = p[2];
    }
    return out;
  };
  const out: CollisionShape[] = [];
  const refusals: string[] = [];
  for (const sh of shapes) {
    switch (sh.kind) {
      case 'hull':
        out.push({ kind: 'hull', points: arr(sh.points) });
        break;
      case 'trimesh':
        out.push({ kind: 'trimesh', vertices: arr(sh.vertices), indices: sh.indices });
        break;
      case 'box': {
        if (uniform) {
          out.push({
            kind: 'box',
            center: pt(sh.center),
            half: [sh.half[0] * s[0], sh.half[1] * s[0], sh.half[2] * s[0]],
            quat: quatMul(q, sh.quat),
          });
          break;
        }
        const box = scaleObb(sh.half, sh.quat, s);
        if (box === null) {
          refusals.push(NON_UNIFORM_REFUSAL('rotated box', s));
          break;
        }
        out.push({ kind: 'box', center: pt(sh.center), half: box.half, quat: quatMul(q, box.quat) });
        break;
      }
      case 'sphere':
        if (!uniform) {
          refusals.push(NON_UNIFORM_REFUSAL('sphere', s));
          break;
        }
        out.push({ kind: 'sphere', center: pt(sh.center), r: sh.r * s[0] });
        break;
      case 'capsule':
        if (!uniform) {
          refusals.push(NON_UNIFORM_REFUSAL('capsule', s));
          break;
        }
        out.push({ kind: 'capsule', a: pt(sh.a), b: pt(sh.b), r: sh.r * s[0] });
        break;
    }
  }
  return { shapes: out, refusals };
}

/**
 * An OBB (`half`, `quat`) under a diagonal scale `s`, or `null` when the result is not a box.
 *
 * The columns of the shape's rotation are `q_i`; the scaled edge directions are `S·q_i`. The image
 * is a box exactly when those three stay mutually perpendicular (see `transformShapes`). The
 * tolerance is RELATIVE — an absolute one would refuse a 30 m box that a 3 cm one accepts.
 */
export function scaleObb(half: Vec3, quat: Quat, s: Vec3): { half: Vec3; quat: Quat } | null {
  const cols: Vec3[] = [
    quatRotate(quat, [1, 0, 0]),
    quatRotate(quat, [0, 1, 0]),
    quatRotate(quat, [0, 0, 1]),
  ].map((c): Vec3 => [c[0] * s[0], c[1] * s[1], c[2] * s[2]]);
  const len = cols.map((c) => Math.hypot(c[0], c[1], c[2]));
  const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const EPS = 1e-6;
  for (const [i, j] of [
    [0, 1],
    [0, 2],
    [1, 2],
  ] as const) {
    const l = len[i]! * len[j]!;
    if (l === 0 || Math.abs(dot(cols[i]!, cols[j]!)) > EPS * l) return null;
  }
  const u = cols.map((c, i): Vec3 => [c[0] / len[i]!, c[1] / len[i]!, c[2] / len[i]!]);
  return {
    half: [half[0] * len[0]!, half[1] * len[1]!, half[2] * len[2]!],
    quat: quatFromBasis(u[0]!, u[1]!, u[2]!),
  };
}

/**
 * A unit quaternion from three orthonormal COLUMN vectors (`three`'s `Matrix4.decompose` method,
 * written out because `three` may not enter this file). The largest-diagonal branch is what keeps
 * it stable at 180° turns, where the naive `w`-first form divides by ~0.
 */
export function quatFromBasis(x: Vec3, y: Vec3, z: Vec3): Quat {
  const [m00, m10, m20] = x;
  const [m01, m11, m21] = y;
  const [m02, m12, m22] = z;
  const trace = m00 + m11 + m22;
  if (trace > 0) {
    const k = 0.5 / Math.sqrt(trace + 1);
    return [(m21 - m12) * k, (m02 - m20) * k, (m10 - m01) * k, 0.25 / k];
  }
  if (m00 > m11 && m00 > m22) {
    const k = 2 * Math.sqrt(1 + m00 - m11 - m22);
    return [0.25 * k, (m01 + m10) / k, (m02 + m20) / k, (m21 - m12) / k];
  }
  if (m11 > m22) {
    const k = 2 * Math.sqrt(1 + m11 - m00 - m22);
    return [(m01 + m10) / k, 0.25 * k, (m12 + m21) / k, (m02 - m20) / k];
  }
  const k = 2 * Math.sqrt(1 + m22 - m00 - m11);
  return [(m02 + m20) / k, (m12 + m21) / k, 0.25 * k, (m10 - m01) / k];
}

// ── The sentences ──────────────────────────────────────────────────────────────────────────────

/** The `ucx` warning, verbatim — the Details badge and the overlay's red outline key on it. */
export const UCX_NONE_FOUND = 'declared UCX, none found';
/** The Details badge for a placement that resolved to `none`. */
export const WALKS_THROUGH = 'walks through';

/** `From placement — resolves to box` / `From asset — resolves to trimesh (default)`. */
export function describeCollisionSource(mode: AssetCollisionMode, from: CollisionFrom): string {
  const origin = from === 'placement' ? 'From placement' : 'From asset';
  return `${origin} — resolves to ${mode}${from === 'default' ? ' (default)' : ''}`;
}

/**
 * `103 trimeshes · 262,267 tris · cooked · built in 190 ms` — the line the Details panel shows.
 *
 * The origin word is ONE word and it lives here rather than in `describeCollisionSource`: two
 * provenances, two lines. An artist reading `runtime` on a world that HAS been cooked is reading a
 * refusal, and the verbatim warning immediately below names which leg refused it.
 */
export function describeCollisionBuild(info: EntityCollisionInfo): string {
  if (info.mode === 'none' || info.colliders === 0) return 'no collider — the player walks through this placement';
  const noun = info.mode === 'trimesh' ? 'trimesh' : info.mode === 'box' ? 'box' : 'shape';
  const n = info.colliders;
  const parts = [`${n} ${noun}${n === 1 ? '' : noun === 'shape' ? 's' : 'es'}`];
  if (info.tris > 0) parts.push(`${info.tris.toLocaleString('en-US')} tris`);
  parts.push(info.origin);
  parts.push(`built in ${Math.round(info.buildMs)} ms`);
  return parts.join(' · ');
}

/** The chips a Details header shows for a bound placement — `[]` when nothing is wrong. */
export function collisionBadges(info: EntityCollisionInfo): string[] {
  const out: string[] = [];
  if (info.warnings.some((w) => w.includes(UCX_NONE_FOUND))) out.push(UCX_NONE_FOUND);
  if (info.mode === 'none' || info.colliders === 0) out.push(WALKS_THROUGH);
  return out;
}
